import { GameState, Position, Difficulty } from '../types/game';
import { getValidMoves, isValidMove, makeReversiMove } from './reversiLogic';

// 黑白棋位置权重表
const POSITION_WEIGHTS = [
  [100, -20, 10, 5, 5, 10, -20, 100],
  [-20, -50, -2, -2, -2, -2, -50, -20],
  [10, -2, -1, -1, -1, -1, -2, 10],
  [5, -2, -1, -1, -1, -1, -2, 5],
  [5, -2, -1, -1, -1, -1, -2, 5],
  [10, -2, -1, -1, -1, -1, -2, 10],
  [-20, -50, -2, -2, -2, -2, -50, -20],
  [100, -20, 10, 5, 5, 10, -20, 100]
];

// 评估棋盘位置的价值
const evaluatePosition = (board: GameState['board'], position: Position, player: 'black' | 'white'): number => {
  return POSITION_WEIGHTS[position.row][position.col];
};

// 计算棋盘优势
const evaluateBoard = (board: GameState['board'], player: 'black' | 'white'): number => {
  let score = 0;
  const opponent = player === 'black' ? 'white' : 'black';

  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if (board[row][col] === player) {
        score += POSITION_WEIGHTS[row][col];
      } else if (board[row][col] === opponent) {
        score -= POSITION_WEIGHTS[row][col];
      }
    }
  }

  return score;
};

// 获取最佳移动位置
const getBestMove = (gameState: GameState, difficulty: Difficulty): Position | null => {
  const validMoves = getValidMoves(gameState.board, gameState.currentPlayer);

  if (validMoves.length === 0) {
    return null;
  }

  if (difficulty === 'easy') {
    // 简单难度：随机选择，但稍微偏向角落
    const cornerMoves = validMoves.filter(pos =>
      (pos.row === 0 || pos.row === 7) && (pos.col === 0 || pos.col === 7)
    );

    if (cornerMoves.length > 0 && Math.random() < 0.6) {
      return cornerMoves[Math.floor(Math.random() * cornerMoves.length)];
    }

    return validMoves[Math.floor(Math.random() * validMoves.length)];
  }

  if (difficulty === 'medium') {
    // 中等难度：基于位置权重选择
    let bestMove = validMoves[0];
    let bestScore = -Infinity;

    for (const move of validMoves) {
      const score = evaluatePosition(gameState.board, move, gameState.currentPlayer);

      // 添加一些随机性
      const finalScore = score + (Math.random() - 0.5) * 20;

      if (finalScore > bestScore) {
        bestScore = finalScore;
        bestMove = move;
      }
    }

    return bestMove;
  }

  // 困难难度：考虑下一步对手的应对
  let bestMove = validMoves[0];
  let bestScore = -Infinity;

  for (const move of validMoves) {
    // 模拟这步棋
    const simulatedState = makeReversiMove(gameState, move);

    // 评估当前局面
    let score = evaluateBoard(simulatedState.board, gameState.currentPlayer);

    // 考虑对手的应对（简单版）
    const opponentMoves = getValidMoves(simulatedState.board, simulatedState.currentPlayer);
    if (opponentMoves.length > 0) {
      // 模拟对手最佳应对
      const opponentBestMove = getBestMoveForPlayer(simulatedState, simulatedState.currentPlayer);
      if (opponentBestMove) {
        const opponentSimulatedState = makeReversiMove(simulatedState, opponentBestMove);
        score -= evaluateBoard(opponentSimulatedState.board, simulatedState.currentPlayer) * 0.5;
      }
    }

    // 位置权重加分
    score += evaluatePosition(gameState.board, move, gameState.currentPlayer);

    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }

  return bestMove;
};

// 为指定玩家获取最佳移动（用于困难难度评估对手应对）
const getBestMoveForPlayer = (gameState: GameState, player: 'black' | 'white'): Position | null => {
  const validMoves = getValidMoves(gameState.board, player);

  if (validMoves.length === 0) {
    return null;
  }

  let bestMove = validMoves[0];
  let bestScore = -Infinity;

  for (const move of validMoves) {
    const score = evaluatePosition(gameState.board, move, player);
    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }

  return bestMove;
};

// 模拟AI思考时间
export const simulateReversiAIThinking = async (difficulty: Difficulty): Promise<void> => {
  let delay: number;

  switch (difficulty) {
    case 'easy':
      delay = 200 + Math.random() * 300; // 0.2-0.5秒
      break;
    case 'medium':
      delay = 400 + Math.random() * 400; // 0.4-0.8秒
      break;
    case 'hard':
      delay = 600 + Math.random() * 600; // 0.6-1.2秒
      break;
    default:
      delay = 500;
  }

  return new Promise(resolve => setTimeout(resolve, delay));
};

// 获取AI决策
export const getReversiAIDecision = (gameState: GameState, difficulty: Difficulty): Position | null => {
  console.log('Reversi AI: Making decision for difficulty:', difficulty);
  const decision = getBestMove(gameState, difficulty);
  console.log('Reversi AI: Decision made:', decision);
  return decision;
};